import React, { useMemo, useState, useCallback } from 'react';
import { DataGridPremium, GridToolbarContainer, useGridApiRef, GridCellParams, GridColDef, GridColumnGroupingModel, GridRenderCellParams } from '@mui/x-data-grid-premium';
import { Box, IconButton, Snackbar, Alert, Tooltip, LinearProgress } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import axiosInstance from "../AxiosInstance";
import { isForecastWeekInPast } from "../Utilities/forecastWeekEditability";
import { formatWeekHeader } from "../Utilities/weekNumberUtils";
import { 
    WeeklyDistributionRate, 
    SkuBaseForecastRow, 
    PromotionProduct, 
    SnackbarState, 
    EditedRow, 
    RateOfSaleOverride,
    getChangedRateOfSales,
    updateWeeklyDistributionRates,
    handleApiError
} from './shared/forecastTableUtils';
import ResetDialog from './shared/ResetDialog';
import OverrideIndicatorCell from './shared/OverrideIndicatorCell';
import { useResetDialog } from './shared/useResetDialog';
import './SkuBaseForecastTable.css';

interface RateOfSaleForecastTableProps {
    forecastId: string;
    forecastPeriodId: string;
    isLocked: boolean;
    promotionProductsData: PromotionProduct[];
    allowPastWeeks?: boolean;
    rows: SkuBaseForecastRow[];
    onReload: () => Promise<void>;
    onRefreshPromotions?: () => Promise<void>;
}

const RateOfSaleForecastTable: React.FC<RateOfSaleForecastTableProps> = ({
    forecastId,
    forecastPeriodId,
    isLocked,
    promotionProductsData,
    allowPastWeeks = false,
    rows,
    onReload,
    onRefreshPromotions
}) => {
    const apiRef = useGridApiRef();
    const [editedRows, setEditedRows] = useState<Record<string, EditedRow>>({});
    const [unsavedCellChanges, setUnsavedCellChanges] = useState<Set<string>>(new Set());
    const [snackbar, setSnackbar] = useState<SnackbarState | null>(null);

    const { resetDialog, handleResetDialogClose, handleResetConfirm, openResetDialog } = useResetDialog({
        rows,
        editedRows,
        setEditedRows,
        apiRef,
        setSnackbar,
        fieldType: 'rateOfSale',
        setUnsavedCellChanges
    });

    const handleCloseSnackbar = () => setSnackbar(null);

    const handleSaveClick = async () => {
        try {
            const overrides = Object.values(editedRows).flatMap(({ original, updated }) => {
                return getChangedRateOfSales(
                    updated.weeklyDistributionRates,
                    original.weeklyDistributionRates
                ).map(change => ({
                    skuBaseForecastId: updated.id,
                    weekStart: change.weekStart,
                    rateOfSale: change.rateOfSale
                }));
            });

            const payload = {
                rateOfSaleOverrides: overrides,
                forecastPeriodId
            };

            if (payload.rateOfSaleOverrides.length === 0) return;

            await axiosInstance.post(`/forecasts/${forecastId}/skubaseforecasts/rateofsales/bulk`, payload);

            setSnackbar({ children: 'Rate of Sales updated successfully.', severity: 'success' });
            setEditedRows({});
            setUnsavedCellChanges(new Set());
            await onReload();
            // Also refresh promotions if visible/connected
            if (onRefreshPromotions) {
                try {
                    await onRefreshPromotions();
                } catch (e: any) {
                    const message = (e && (e.message || (e.response && e.response.data))) || 'Failed to refresh promotions.';
                    setSnackbar({ children: `Promotions refresh failed: ${message}`, severity: 'error' });
                }
            }
        } catch (error) {
            const errorMessage = handleApiError(error);
            setSnackbar({ children: errorMessage, severity: 'error' });
        }
    };

    const CustomToolbar: React.FC = () => (
        <GridToolbarContainer sx={{ justifyContent: 'flex-end' }}>
            <Tooltip title="Save">
                <span>
                    <IconButton
                        aria-label="save"
                        onClick={handleSaveClick}
                        disabled={Object.keys(editedRows).length === 0}
                    >
                        <SaveIcon />
                    </IconButton>
                </span>
            </Tooltip>
        </GridToolbarContainer>
    );

    const processRowUpdate = (newRow: SkuBaseForecastRow, oldRow: SkuBaseForecastRow): SkuBaseForecastRow => {
        const validationErrors: string[] = [];
        let updatedRow: SkuBaseForecastRow = { ...newRow };

        if (!updatedRow.weeklyDistributionRates) return oldRow;

        // Process dynamic week fields and update weekly distribution rates
        Object.keys(updatedRow).forEach((key) => {
            if (key !== 'id' && key !== 'skuDescription' && key !== 'weeklyDistributionRates') {
                const value = (updatedRow as any)[key];
                if (typeof value === 'string' || typeof value === 'number') {
                    updatedRow = updateWeeklyDistributionRates(updatedRow, key, value.toString(), 'rateOfSale');
                    // Remove the dynamic property after processing
                    delete (updatedRow as any)[key];
                }
            }
        });

        const changedRateOfSales = getChangedRateOfSales(updatedRow.weeklyDistributionRates, oldRow.weeklyDistributionRates);

        // Validate and reset invalid values
        for (const change of changedRateOfSales) {
            if (change.rateOfSale !== null && change.rateOfSale < 0) {
                validationErrors.push(`Invalid Rate of Sale for week ${change.weekStart} - must be non-negative`);
                const originalWeek = oldRow.weeklyDistributionRates.find(w => w.week === change.weekStart);
                if (originalWeek) {
                    updatedRow = updateWeeklyDistributionRates(updatedRow, change.weekStart, originalWeek.rateOfSale?.toString() || '0', 'rateOfSale');
                }
            }
        }

        if (validationErrors.length > 0) {
            setSnackbar({ children: validationErrors.join('\n'), severity: 'error' });
        }

        // Always update editedRows when a row is processed, even if no changes
        setEditedRows(prev => ({
            ...prev,
            [updatedRow.id]: {
                original: oldRow,
                updated: updatedRow,
            }
        }));

        // Track unsaved cell changes
        if (changedRateOfSales.length > 0) {
            setUnsavedCellChanges(prev => {
                const newSet = new Set(prev);
                changedRateOfSales.forEach(change => {
                    newSet.add(`${updatedRow.id}-${change.weekStart}`);
                });
                return newSet;
            });
        }

        return updatedRow;
    };

    const handleProcessRowUpdateError = useCallback((error: Error) => {
        setSnackbar({ children: error.message, severity: 'error' });
    }, []);

    const isCellEditable = (params: GridCellParams): boolean => {
        const { field } = params;
        return !(isForecastWeekInPast(field, allowPastWeeks) || isLocked);
    };

    const { columns, columnGroupingModel } = useMemo(() => {
        const monthMap = new Map<string, Array<{ field: string }>>();
        const weeklyColumns: GridColDef[] = rows.length && rows[0]?.weeklyDistributionRates
            ? rows[0].weeklyDistributionRates.map((wdr, index) => {
                const customMonth = wdr.forecastWeek.month;
                const monthName = new Date(2000, customMonth - 1, 1)
                    .toLocaleString('default', { month: 'short' })
                    .toUpperCase();

                const column: GridColDef = {
                    field: wdr.week,
                    headerName: formatWeekHeader(wdr.week, index),
                    type: 'number',
                    width: 100,
                    editable: true,
                    valueGetter: (value, row) => row.weeklyDistributionRates.find((w: WeeklyDistributionRate) => w.week === wdr.week)?.rateOfSale,
                    cellClassName: (params: GridCellParams) => {
                        const classes: string[] = [];

                        const isPastOrLocked = isForecastWeekInPast(params.field, allowPastWeeks) || isLocked;

                        const isHighlighted =
                            promotionProductsData &&
                            promotionProductsData.some(pp =>
                                pp.skuBaseForecastId === params.id &&
                                pp.weeklyDistributionRates?.includes(params.field)
                            );

                        if (isHighlighted) {
                            classes.push('highlightedCell');
                        }

                        if (isPastOrLocked) {
                            classes.push('nonEditableCell');
                        }

                        return classes.join(' ');
                    },
                    valueFormatter: (value: any) => {
                        return parseFloat(value?.toString() || '0').toString();
                    },
                    renderCell: (params: GridRenderCellParams) => {
                        const weekData = params.row.weeklyDistributionRates.find((w: WeeklyDistributionRate) => w.week === params.field);
                        const hasOverride = (weekData as any)?.hasRateOfSaleOverride || false;
                        const value = weekData?.rateOfSale || 0;
                        const isPastOrLocked = isForecastWeekInPast(params.field, allowPastWeeks) || isLocked;
                        
                        const currentWeekIndex = params.row.weeklyDistributionRates.findIndex((w: WeeklyDistributionRate) => w.week === params.field);
                        const baseValue = currentWeekIndex > 0 
                            ? params.row.weeklyDistributionRates[currentWeekIndex - 1].rateOfSale || 0
                            : 0;
                        
                        const cellKey = `${params.id}-${params.field}`;
                        const hasUnsavedChanges = unsavedCellChanges.has(cellKey);
                        
                        return (
                            <OverrideIndicatorCell
                                value={value}
                                hasOverride={hasOverride}
                                isPastOrLocked={isPastOrLocked}
                                hasUnsavedChanges={hasUnsavedChanges}
                                onDotClick={() => openResetDialog(params.id, params.field as string, value, baseValue)}
                            />
                        );
                    }
                };

                if (!monthMap.has(monthName)) {
                    monthMap.set(monthName, []);
                }
                monthMap.get(monthName)!.push({ field: wdr.week });

                return column;
            })
            : [];

        const columnGroupingModel: GridColumnGroupingModel = Array.from(monthMap.entries()).map(([groupId, children]) => ({
            groupId,
            headerName: groupId,
            children
        }));

        const baseColumns: GridColDef[] = [
            { field: 'id', headerName: 'ID', width: 90 },
            { field: 'skuDescription', headerName: 'SKU', width: 300 }
        ];

        return {
            columns: [...baseColumns, ...weeklyColumns],
            columnGroupingModel
        };
    }, [rows, isLocked, promotionProductsData, unsavedCellChanges]);

    return (
        <Box sx={{ height: '100%', width: '100%' }}>
            <DataGridPremium
                apiRef={apiRef}
                editMode="row"
                rows={rows}
                columns={columns}
                columnGroupingModel={columnGroupingModel}
                experimentalFeatures={{ columnGrouping: true } as any}
                isCellEditable={isCellEditable}
                processRowUpdate={processRowUpdate}
                onProcessRowUpdateError={handleProcessRowUpdateError}
                slots={{ toolbar: CustomToolbar, loadingOverlay: LinearProgress as any }}
                initialState={{
                    columns: {
                        columnVisibilityModel: {
                            id: false,
                        },
                    },
                    pinnedColumns: { left: ['skuDescription'] }
                }}
                cellSelection
                disableClipboardPaste
            />
            {!!snackbar && (
                <Snackbar
                    open
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                    onClose={handleCloseSnackbar}
                    autoHideDuration={6000}
                >
                    <Alert {...snackbar} onClose={handleCloseSnackbar} />
                </Snackbar>
            )}
            <ResetDialog
                open={resetDialog.open}
                currentValue={resetDialog.currentValue}
                baseValue={resetDialog.baseValue}
                onClose={handleResetDialogClose}
                onConfirm={handleResetConfirm}
            />
        </Box>
    );
};

export default RateOfSaleForecastTable;
